import {AGENCY_KEYS} from "@/api/agencyApi";
import {useCompanyListQuery} from "@/api/companyApi";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import TrashIcon from "@/assets/colorable/trash.svg?react";
import CompanyLogoPlaceholder from "@/assets/images/company_logo_placeholder.svg";
import EmptyState from "@/components/Campaign/management/components/EmptyState";
import Loader from "@/components/Common/kpi/Loader";
import AgencyListFilters from "@/components/MultiAccount/AgencyListFilters";
import CompanyListFilters from "@/components/MultiAccount/CompanyListFilters";
import DeleteBrandAccountModal from "@/components/MultiAccount/DeleteBrandAccountModal";
import type {AgencySerializer} from "@/generated/DjangoTypes";
import {useInViewWithQuery} from "@/hooks/useInViewWithQuery";
import {getPackageIcon, packages} from "@/hooks/usePackages";
import {useGenericInfiniteQueryWithCount} from "@/hooks/useQueryHooks";
import {useCategoryStore} from "@/store/category";
import {FULL_DATE_FORMAT} from "@/store/constants";
import useCustomerFilterStore from "@/store/customerFilter";
import type {TSingleBrandCustomer} from "@/store/types";
import {isActiveAdminAssignedTrial} from "@/utils/planUtils";
import {createFileRoute, Link} from "@tanstack/react-router";
import dayjs from "dayjs";
import {snakeCase} from "lodash";
import {Fragment, useState} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/_admin/admins/customers")({
  component: CustomerList,
});

function CustomerList() {
  const {t} = useTranslation(["customer_list", "navigation"]);

  useTitle(t("navigation:customers"));

  const categories = useCategoryStore(state => state.categories);
  const searchParams = useCustomerFilterStore(state => state.searchParams);
  const resetFilters = useCustomerFilterStore(state => state.resetFilters);
  const type = useCustomerFilterStore(state => state.filters.type);
  const [brandToDelete, setBrandToDelete] = useState<TSingleBrandCustomer | null>(null);

  const agenciesQuery = useGenericInfiniteQueryWithCount<AgencySerializer>({
    queryKey: AGENCY_KEYS.qAgencyList(searchParams),
    enabled: type === "agencies",
  });
  const {ref: agencyListRef} = useInViewWithQuery(agenciesQuery);
  const subBrandsQuery = useCompanyListQuery({
    params: searchParams,
    queryOptions: {
      enabled: type === "sub_brands",
    },
  });
  const {ref: subBrandListRef} = useInViewWithQuery(subBrandsQuery);

  const query = type === "agencies" ? agenciesQuery : subBrandsQuery;

  return (
    <div className="max-w-wide container mx-auto mt-8 mb-20 flex flex-col gap-4 px-8">
      <div className="flex items-center gap-4">
        <button
          onClick={() => resetFilters("sub_brands")}
          className={
            "border-neutral-gray-dark flex flex-row items-center gap-2 rounded-md border p-2 text-sm font-semibold transition-all" +
            (type === "sub_brands" ? " border-primary-dark-solid bg-primary-light-solid" : " bg-white")
          }>
          {t("company")}
        </button>
        <button
          onClick={() => resetFilters("agencies")}
          className={
            "border-neutral-gray-dark flex flex-row items-center gap-2 rounded-md border p-2 text-sm font-semibold transition-all" +
            (type === "agencies" ? " border-primary-dark-solid bg-primary-light-solid" : " bg-white")
          }>
          {t("agency_or_large_brand")}
        </button>
      </div>
      {type === "agencies" ? <AgencyListFilters /> : <CompanyListFilters />}
      <div className="flex items-center justify-end gap-2">
        {query.isPending ? (
          <div className="border-primary-light-solid rounded-md border p-3">
            <Loader />
          </div>
        ) : (
          <span className="border-primary-light-solid text-primary-dark-solid text-2.5xl rounded-md border p-3 font-semibold">
            {query.data?.count}
          </span>
        )}
      </div>
      {type === "agencies" && (
        <>
          {!agenciesQuery.isFetching && !agenciesQuery.data?.count && <EmptyState title={t("no_agencies_to_show")} />}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {agenciesQuery.data?.results?.map(agency => (
              <Link
                key={agency.id}
                to="/agency/$agencyId/customers"
                params={{agencyId: agency.id}}
                className="border-primary-light-solid hover:bg-neutral-grey-lightest flex flex-col items-center gap-3 rounded-md border bg-white p-5 no-underline">
                <img
                  src={agency.picture320 ?? agency.picture ?? CompanyLogoPlaceholder}
                  className="border-primary-light-solid size-25 shrink-0 rounded-full border"
                />
                <span className="text-black-bluetint text-center text-xl font-semibold hyphens-auto">
                  {agency.name}
                </span>
              </Link>
            ))}
            {agenciesQuery.isFetching && (
              <Loader
                styles={{
                  containerClassName: "my-auto",
                  loaderClassName: "min-h-40",
                }}
              />
            )}
            {agenciesQuery.hasNextPage && !agenciesQuery.isFetching && <div ref={agencyListRef} />}
          </div>
        </>
      )}
      {type === "sub_brands" && (
        <>
          {!subBrandsQuery.isFetching && !subBrandsQuery.data?.count && (
            <EmptyState title={t("no_companies_to_show")} />
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {subBrandsQuery.data?.results?.map(brand => {
              const subscription = brand.agency?.package_subscription;
              const hasActiveSubscription = !!subscription && dayjs(subscription.valid_until).isSameOrAfter(dayjs());

              return (
                <Link
                  key={brand.id}
                  to="/company/$companyId"
                  params={{companyId: brand.id}}
                  className="border-primary-light-solid hover:bg-neutral-grey-lightest flex flex-col items-center gap-3 rounded-md border bg-white p-5 no-underline">
                  {!!subscription ? (
                    <div className="flex items-center justify-between self-stretch">
                      <div className="border-neutral-grey-light-dark flex size-4.5 shrink-0 items-center justify-center rounded-full border bg-white">
                        <div
                          className={`size-3.5 shrink-0 rounded-full ${!!hasActiveSubscription ? "bg-success-medium-green" : "bg-neutral-grey-light"}`}
                        />
                      </div>
                      <span className="text-neutral-grey-dark text-xs">
                        {t(subscription?.is_admin_assigned ? "admin" : "stripe")}
                      </span>
                    </div>
                  ) : (
                    <div className="flex h-4.5 items-center justify-end self-stretch">
                      {!!brand.agency?.id && (
                        <button
                          title={t("delete_brand_account")}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setBrandToDelete(brand);
                          }}
                          className="text-neutral-grey-dark hover:text-danger-medium-red">
                          <TrashIcon className="size-4" />
                        </button>
                      )}
                    </div>
                  )}
                  <img
                    src={brand.picture320 ?? brand.picture ?? CompanyLogoPlaceholder}
                    className="border-primary-light-solid size-25 shrink-0 rounded-full border"
                  />
                  <div className="flex flex-col gap-1">
                    <span className="text-black-bluetint line-clamp-3 text-center text-xl font-semibold hyphens-auto">
                      {brand.company_name ?? "-"}
                    </span>
                    <div className="flex flex-wrap items-center justify-center gap-1">
                      {brand.interests?.map((id, idx, arr) => {
                        const interest = categories.find(c => c.id === id);

                        return (
                          <Fragment key={interest?.id}>
                            <span className="text-neutral-grey-dark text-2xs leading-3 font-bold uppercase">
                              {interest?.name}
                            </span>
                            {idx < arr.length - 1 && (
                              <span className="bg-neutral-grey-dark inline-block size-1 shrink-0 rounded-full" />
                            )}
                          </Fragment>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grow" />
                  {subscription && (
                    <div className="border-primary-light-solid bg-neutral-grey-lightest flex w-full flex-col items-center gap-0.5 rounded-md border px-3 py-2">
                      <div className="flex items-center gap-2">
                        {getPackageIcon({packageType: subscription.name!, className: "size-4 shrink-0"})}
                        <span className="text-neutral-black text-xs">
                          {t(snakeCase(packages[subscription.name!].translationKey) as "package_advanced")}
                        </span>
                        <div className="flex items-center gap-1">
                          {subscription?.instagram_campaigns && <InstagramIcon className="h-2.5 w-auto shrink-0" />}
                          {subscription?.tiktok_campaigns && <TiktokIcon className="h-2.5 w-auto shrink-0" />}
                        </div>
                      </div>
                      <span className="text-neutral-grey-dark text-2xs">
                        {dayjs(subscription.valid_from).format(FULL_DATE_FORMAT)} -{" "}
                        {dayjs(subscription.valid_until).format(FULL_DATE_FORMAT)}
                      </span>
                      {isActiveAdminAssignedTrial(subscription) && (
                        <span className="text-warning-dark-yellow text-2xs font-semibold uppercase">{t("trial")}</span>
                      )}
                      {subscription.status === "canceled" && (
                        <span className="text-danger-dark-red text-2xs font-semibold uppercase">{t("cancelled")}</span>
                      )}
                    </div>
                  )}
                </Link>
              );
            })}
            {subBrandsQuery.isFetching && (
              <Loader
                styles={{
                  containerClassName: "my-auto",
                  loaderClassName: "min-h-40",
                }}
              />
            )}
            {subBrandsQuery.hasNextPage && !subBrandsQuery.isFetching && <div ref={subBrandListRef} />}
          </div>
        </>
      )}
      {brandToDelete && <DeleteBrandAccountModal brand={brandToDelete} onClose={() => setBrandToDelete(null)} />}
    </div>
  );
}
